import React, { useEffect, useMemo, useRef, useState } from 'react';
import TimePeriod from './TimePeriod';
import Search from './Search/Search';
import { getEmptyGuid, createNewGuid, ShowRememberSubmitCallout } from './Utils';
import { TimeGridMode } from '../types/TimeGridMode';
import { Label, MessageBar, MessageBarType, PrimaryButton, Stack, useTheme } from '@fluentui/react';
import { Dialog, DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import TimeStatePicker from './TimeStatePicker';
import PeriodPicker from './PeriodPicker';
import { QuotesLoader } from './QuotesLoader';
import { PeriodDay } from '../types/PeriodDay';
import { CellContextProvider } from './Contexts/CellContext';
import { GetValidatorParameters } from '../Validation/helpers';
import { ValidateResponse, Validator } from '../Validation/Validator';
import { ValidationWarningDialog } from '../Validation/Components/ValidationWarningDialog';
import { PageHeader } from './LayoutElements/PageHeader';
import { PageContent } from './LayoutElements/PageContent';
import { ContentSection } from './LayoutElements/ContentSection';
import { InformationDialog } from './InformationDialog';
import { TimeInformation } from '../types/TimeInformation';
import { ValidationErrors } from '../Validation/Components/ValidationErrors';
import { TFTMainCoachmarks } from './Guide/Coachmarks/TFTMainCoachmarks';
import { TimePeriodWeekOverview } from './WeekOverview/TimePeriodWeekOverview';
import { ApiCalls } from '../api/api';
import { SetPinnedDto, SetUserPeriodStateDto, UserRegistrationPeriod, UserReportingPeriodState } from '../api/generated/data-contracts';
import { useUiContext } from './Contexts/UIContext/uiCtx';
import { useGridContext } from './Contexts/GridContext/context';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAllPeriods } from '../hooks/queries/useAllPeriods';
import { useConfigValuesState } from '../hooks/useConfigValue';
import { useTranslation } from 'react-i18next';
import { useGetCurrentPeriod } from '../hooks/queries/useGetCurrentPeriod';
import { updateReportPeriod } from '../helpers/updateReportPeriod';
import { useCopyTimesheetWeek } from '../hooks/useCopyTimesheetWeek';

const currentday = new Date();

function Time() {
    const theme = useTheme();
    const { isTeams } = useUiContext();
    const { t } = useTranslation('timesheet');
    const queryClient = useQueryClient();

    const [today, setToday] = useState<Date>(null);
    const [mytimeprojects, setMytimeprojects] = useState<UserRegistrationPeriod[]>([]);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [periodIsClosed, setPeriodIsClosed] = useState<boolean>(false);
    const [loadingcomplete, setLoadingcomplete] = useState<boolean>(false);
    const [periodloadingcomplete, setPeriodloadingcomplete] = useState<boolean>(true);
    const [hideDublicateTimeDlg, setHideDublicateTimeDlg] = useState<boolean>(true);
    const [timestate, setTimestate] = useState<UserReportingPeriodState>('Open');
    const [periodStart, setPeriodStart] = React.useState<Date>(new Date());
    const [periodDeadlineDate, setPeriodDeadlineDate] = React.useState<Date>(null);
    const [searchActivate, setSearchActivate] = React.useState<number>(0);
    const [comment, setComment] = useState<string>('');
    const [submitComment, setSubmitComment] = useState<string>('');
    const [periodNotCreated, setPeriodNotCreated] = useState(null);
    const [validationErrors, setValidationErrors] = useState<ValidateResponse[]>([]);
    const [showValidationWarningDialog, setShowValidationWarningDialog] = useState<boolean>(false);
    const [showInformationDialog, setShowInformationDialog] = useState<boolean>(false);
    const [informationDialogInformation, setInformationDialogInformation] = useState<TimeInformation>(null);

    const [lastPeriodEnd, setLastPeriodEnd] = React.useState<Date>(null);
    const [lastPeriodState, setLastPeriodState] = React.useState<UserReportingPeriodState>('Open');
    const { setSelectedTimePeriod, selectedTimePeriod } = useGridContext();

    const [selectedDate, setSelectedDate] = useState<Date>(currentday);
    const { currentPeriod, isLoading } = useGetCurrentPeriod(selectedDate.getISOStringMidnight());

    const validator = useRef(new Validator()).current;

    const {
        initialData: {
            configuration,
            aggregatedUser: { user },
            allowedWorkTypes,
            projectTypes,
        },
    } = useConfigValuesState();
    const { timeSheetValidation, gridView, enableCoachmarks } = configuration;
    console.log('🚀 ~ Time ~ gridView:', gridView);

    const userCapacity = useMemo(() => user.availability ?? user.calendar?.dailyCapacity ?? configuration.capacityDefault, [user, configuration]);

    useEffect(() => {
        //MOVE
        (window as any).validator = validator;

        const rules = Object.entries(timeSheetValidation)
            .filter(([key, value]) => value && typeof value === 'boolean')
            .map(([key]) => key);

        rules.forEach(rule => {
            validator.addRule(rule);
        });
    }, []);

    const { data: allPeriods = null } = useAllPeriods({ suspense: true });

    const { mutateCopyTimesheet } = useCopyTimesheetWeek();

    useEffect(() => {
        console.debug('Time.tsx ready');
        setLoadingcomplete(false);
    }, []);

    const selectedDateUpdated = (date: Date) => {
        updateReportPeriod(mytimeprojects, timestate, selectedDate, queryClient);

        setSelectedDate(date);
    };

    useEffect(() => {
        console.log('CURRENT', currentPeriod);
        if (!isLoading) {
            setPeriodloadingcomplete(false);
            setShowValidationWarningDialog(false);
            setValidationErrors([]);

            setPeriodNotCreated(currentPeriod === null);

            if (currentPeriod === null) {
                setLoadingcomplete(true);
                return;
            } else {
                if (currentPeriod.reportPeriod) {
                    setComment(currentPeriod.reportPeriod.comment ?? '');
                    setSubmitComment(currentPeriod.reportPeriod.submitComment ?? '');

                    setPeriodStart(new Date(currentPeriod.reportPeriod.startDate));
                    setPeriodDeadlineDate(new Date(currentPeriod.reportPeriod.deadlineDate));

                    setPeriodIsClosed(currentPeriod.reportPeriod.isClosed);

                    console.log('🚀 ~ .then ~ currentperiod.state:', currentPeriod.reportPeriod.state);
                    setTimestate(currentPeriod.reportPeriod.state);

                    setSubmitted(currentPeriod.reportPeriod.state !== 'Open');

                    const nextPeriodStartDate = new Date(currentPeriod.reportPeriod.endDate);
                    nextPeriodStartDate.setDate(nextPeriodStartDate.getDate() + 1);

                    setSelectedTimePeriod({
                        periodId: currentPeriod.reportPeriod.periodId,
                        name: currentPeriod.reportPeriod.name,
                        reportPeriodId: currentPeriod.reportPeriod.reportPeriodId,
                        state: currentPeriod.reportPeriod.state,
                        startDate: new Date(currentPeriod.reportPeriod.startDate),
                        endDate: new Date(currentPeriod.reportPeriod.endDate),
                        isClosed: currentPeriod.reportPeriod.isClosed,
                        deadlineDate: new Date(currentPeriod.reportPeriod.deadlineDate),
                        workDays: currentPeriod.reportPeriod.workDays,
                        nextPeriodStartDate: nextPeriodStartDate,
                    });
                }

                setToday(currentday);

                if (currentPeriod.timesheetData) {
                    setMytimeprojects(currentPeriod.timesheetData.rows);
                }

                if (currentPeriod.lastReportPeriod) {
                    setLastPeriodEnd(new Date(currentPeriod.lastReportPeriod.endDate));
                    console.debug('prior period to the selected one', currentPeriod.lastReportPeriod);

                    setLastPeriodState(currentPeriod.lastReportPeriod.state);
                }

                setPeriodloadingcomplete(true);
                setLoadingcomplete(true);
            }
        }
    }, [isLoading, currentPeriod, setSelectedTimePeriod]);

    const addTaskFromSearch = (time: UserRegistrationPeriod, pinned: boolean, mode: TimeGridMode) => {
        setMytimeprojects(timeTasks =>
            [...timeTasks, time].sort((a: UserRegistrationPeriod, b: UserRegistrationPeriod) => (a.projectName > b.projectName ? -1 : 1)),
        );
    };

    const { mutate } = useMutation((data: SetPinnedDto) => ApiCalls.pinForUser(data));

    const pinTime = (time: UserRegistrationPeriod, pinned: boolean) => {
        mutate({ taskId: time.taskId, pinned, workTypeId: time.workType?.id });

        //We need to update the state (This is used to chose pin state on assignment with new workType)
        setMytimeprojects(mp =>
            mp.map(timeRow => {
                if (timeRow.assignmentId !== time.assignmentId) {
                    return timeRow;
                }
                return { ...timeRow, pinned: pinned };
            }),
        );
    };

    const DeleteTime = (time: UserRegistrationPeriod, perioddays: any[]) => {
        console.debug('Should delete row');

        // DELETE TIME ROW TIME
        const weekDays = perioddays;
        console.log('TotalCellChange should be propagated and saved', 0, 0);

        //Ugly Assignment not created fix
        if (time.assignmentId === getEmptyGuid()) {
            const [firstDay, ...rest] = weekDays;

            const selectedTimeEntry = time.timeEntries?.filter(te => new Date(te.date).getTime() === firstDay.date.getTime())[0];

            onCellUpdated(time, firstDay, selectedTimeEntry, 0, undefined, selectedTimeEntry?.flagged, TimeGridMode.MyTime, 0, () => {
                // setdatasaved(false);
                console.debug('Data saved false');
            });

            setTimeout(() => {
                rest.forEach(async (periodDay, index) => {
                    const selectedTimeEntry = time.timeEntries?.filter(te => new Date(te.date).getTime() === periodDay.date.getTime())[0];

                    onCellUpdated(time, periodDay, selectedTimeEntry, 0, undefined, selectedTimeEntry?.flagged, TimeGridMode.MyTime, 0, () => {
                        console.debug('Data saved false');
                    });
                });
            }, 500);

            return;
        }

        weekDays.forEach(async periodDay => {
            const selectedTimeEntry = time.timeEntries?.filter(te => new Date(te.date).getTime() === periodDay.date.getTime())[0];
            onCellUpdated(time, periodDay, selectedTimeEntry, 0, undefined, selectedTimeEntry?.flagged, TimeGridMode.MyTime, 0, () => {
                console.debug('Data saved false');
            });
        });

        // UNPIN TASK
        mutate({ taskId: time.taskId, pinned: false, workTypeId: time.workType?.id });

        setMytimeprojects(mytimeprojects.filter(_ => _ !== time));
    };

    const onCellUpdated = async (
        time: UserRegistrationPeriod,
        day: PeriodDay,
        te: any,
        value: number,
        comment: string,
        flagged: boolean,
        mode: TimeGridMode,
        prevVal: number,
        saveSuccess: Function,
        skipApiCall?: boolean,
    ): Promise<void> => {
        console.debug('saving', new Date());

        //this is for total update
        if (comment === undefined) comment = te?.comment;

        const response = null;

        // Only make API call if skipApiCall is not true (for TotalCell, API call is already made by useUpdateFromTotalCell)
        if (!skipApiCall) {
            ApiCalls.registerTimeEntry({
                taskId: time.taskId,
                date: day.date.removeTimeZone().toISOString(),
                hours: value,
                comment,
                flagged,
                userReportingPeriodId: selectedTimePeriod.reportPeriodId,
                workTypeId: time.workType?.id || getEmptyGuid(),
            }).then(response => {
                switch (response.data.registrationStatusCode) {
                    case 'Success':
                        if (saveSuccess) {
                            saveSuccess();
                        }
                        break;
                    case 'PeriodIsClosed':
                        throw new Error('Period is closed. The period for the changed timesheet has been closed. Please refresh.');
                    case 'InsufficientPermissions':
                        throw new Error('Insufficient permissions. You do not have permission to do this. Please refresh.');
                    case 'DbError':
                        throw new Error('Database error. Something tried to violate a database constraint. Please refresh and try again.');
                    default:
                        throw new Error(
                            'Unknown Error. An unknown error occoured, please refresh and try again. Report this, if issue continues or reappears.',
                        );
                }
            });
        } else {
            // For TotalCell calls, just call saveSuccess immediately since API call was already made
            if (saveSuccess) {
                saveSuccess();
            }
        }

        //CHF -> This may decrese performance a lot
        setMytimeprojects(mp => {
            // const row = mp.find(_ => _.key === time.key);
            const row = mp.find(_ => _.assignmentId === time.assignmentId);

            //Fix to see if created in db
            // row.timeId = undefined;

            //CHF -> Find another place to do this?
            if (!te) {
                //TODO: FIX!
                te = {
                    id: '',
                    date: day.date,
                    hours: value,
                    comment: comment,
                    flagged: flagged,
                    userReportingPeriod: selectedTimePeriod,
                    assignment: { workType: row.workType },
                };

                row.timeEntries.push(te);
            }

            row.timeEntries = row.timeEntries.map(t => {
                if (new Date(t.date).getDate() !== day.date.getDate()) return t;
                t.hours = value;
                t.comment = comment;
                t.flagged = flagged;
                return t;
            });

            if (prevVal < value) {
                row.actualWork += value - prevVal;
            } else {
                row.actualWork += (prevVal - value) * -1;
            }

            return mp.map(tr => {
                if (tr.assignmentId !== time.assignmentId) return tr;
                return row;
            });
        });
    };

    const recalculateActualWork = (assignmentId: string): void => {
        setMytimeprojects(mp => {
            return mp.map(timeRow => {
                if (timeRow.assignmentId !== assignmentId) {
                    return timeRow;
                }

                // Calculate actualWork from sum of all time entries
                const totalActualWork = timeRow.timeEntries.reduce((sum, entry) => sum + entry.hours, 0);

                return {
                    ...timeRow,
                    actualWork: totalActualWork,
                };
            });
        });
    };

    const handleRemainingWorkUpdate = (assignmentId: string, newRemainingWork: number): void => {
        setMytimeprojects(mp => {
            return mp.map(timeRow => {
                if (timeRow.assignmentId !== assignmentId) {
                    return timeRow;
                }

                return {
                    ...timeRow,
                    remainingWork: newRemainingWork,
                };
            });
        });
    };

    const onCellWeekUpdated = async (
        time: UserRegistrationPeriod,
        periodDays: PeriodDay[],
        te: any,
        cellValue: number,
        allCellsValue: number,
        comment: string,
        flagged: boolean,
        prevVal: number,
    ): Promise<string[]> => {
        //this is for total update
        if (comment === undefined) {
            comment = te?.comment;
        }

        const lastTimeProject = mytimeprojects;

        //CHF -> This may decrese performance a lot
        setMytimeprojects(mp => {
            const row = { ...mp.find(_ => _.assignmentId === time.assignmentId) };

            //Fix to see if created in db
            // row.timeId = undefined;

            //CHF -> Find another place to do this?
            const newTimeEntriesToAdd = [];
            periodDays.forEach(day => {
                if (!row.timeEntries.find(timeEntry => new Date(timeEntry.date).getDate() === day.date.getDate())) {
                    newTimeEntriesToAdd.push({
                        id: '',
                        date: day.date,
                        hours: cellValue,
                        comment: comment,
                        flagged: flagged,
                        userReportingPeriod: selectedTimePeriod,
                        assignment: { workType: row.workType },
                    });
                }
            });
            row.timeEntries = [...row.timeEntries, ...newTimeEntriesToAdd];

            row.timeEntries = row.timeEntries.map(t => {
                return { ...t, hours: cellValue, comment, flagged };
            });

            if (prevVal < allCellsValue) {
                row.actualWork += allCellsValue - prevVal;
            } else {
                row.actualWork += (prevVal - allCellsValue) * -1;
            }

            return mp.map(tr => {
                if (tr.assignmentId !== time.assignmentId) {
                    return tr;
                }
                return row;
            });
        });

        return ApiCalls.registerTimeByPeriodDays({
            taskId: time.taskId,
            days: configuration.weekConfig.filter(day => day.enabled).map(day => day.name),
            hours: allCellsValue,
            comment,
            flagged,
            userReportingPeriodId: selectedTimePeriod.reportPeriodId,
            workTypeId: time.workType?.id || getEmptyGuid(),
        })
            .then(({ data }) => {
                return data;
            })
            .catch(error => {
                setMytimeprojects(lastTimeProject);
                throw error;
            });
    };

    const updateWorkType = async (row, workType, changeCompleted) => {
        try {
            const oldWorkTypeId = !row.workType ? getEmptyGuid() : row.workType.id;

            // Returns a boolean indicating if a worktype change is allowed on the existing assignment.
            // If not, a new assignment is created with the selected worktype.
            const response = await ApiCalls.changeWorkTypeFromKey({ taskId: row.taskId, workTypeId: oldWorkTypeId, newWorkTypeId: workType.id });
            const canChange = response.data;

            // If the backend indicates a change was made (canChange is false), adjust the frontend accordingly
            if (!canChange) {
                // If there are any time entries or time entries with any hour amount, create a new row with the new work type
                if (row.timeEntries.length > 0) {
                    const newTimeRow: UserRegistrationPeriod = {
                        assignmentId: createNewGuid(),
                        // getEmptyGuid(),
                        projectId: row.projectId,
                        projectName: row.projectName,
                        projectNumber: row.projectNumber,
                        projectSource: row.projectSource,
                        projectClosed: false,
                        taskId: row.taskId,
                        taskName: row.taskName,
                        taskStart: row.taskStart,
                        taskEnd: row.taskEnd,
                        taskClosed: false,
                        taskIsDefault: row.taskIsDefault,
                        taskDescription: row.taskDescription,
                        pinned: true,
                        // '',
                        projectOrder: row.projectOrder,
                        taskOrder: row.taskOrder,
                        plannedWork: row.plannedWork,
                        remainingWork: row.remainingWork,
                        actualWork: row.actualWork,
                        timeEntries: [],
                        workType,
                        projectWorkType: row.projectWorkType,
                    };

                    // Add the new row with the updated work type
                    setMytimeprojects(tp => [...tp, newTimeRow]);
                    changeCompleted();

                    return;
                }

                // No time entries, just change the work type
                if (row.timeEntries.length === 0) {
                    setMytimeprojects(tp => {
                        return tp.map(r => {
                            if (r.assignmentId === row.assignmentId) {
                                r.workType = workType;
                            }
                            return r;
                        });
                    });
                    changeCompleted();

                    return;
                }
            }
        } catch (error) {
            console.error('Error changing work type:', error);
            changeCompleted(); // Ensure UI is unlocked even on error
            throw error;
        }
    };

    const { mutate: mutateReportingPeriodState } = useMutation((data: SetUserPeriodStateDto) => ApiCalls.setReportingPeriodState(data));

    //TODO: Refactor to another component
    const handleValidationError = (submit: boolean, submitComment: string = '') => {
        setShowValidationWarningDialog(false);
        if (submit) {
            let state: UserReportingPeriodState = 'Submitted';
            if (!user.approver || user.autoApprove) {
                state = 'Approved';
                setTimeout(() => {
                    setTimestate('Approved');
                    timestatechanged('Approved');
                }, 1000);
            }

            setSubmitted(true);
            setTimestate('Submitted');

            mutateReportingPeriodState({ state, submitComment, userReportingPeriodId: selectedTimePeriod.reportPeriodId });
        } else {
            setTimestate('Open');
        }
    };

    const timestatechanged = async (state: UserReportingPeriodState, submitComment: string = ''): Promise<void> => {
        if (state === 'Submitted') {
            //TODO: Refactor to another component
            const allEntries = mytimeprojects.flatMap(_ => _.timeEntries);
            const validatorParams = GetValidatorParameters(
                allEntries,
                timeSheetValidation,
                mytimeprojects,
                selectedTimePeriod,
                user,
                configuration,
                allowedWorkTypes,
                projectTypes,
                userCapacity,
            );
            try {
                const validationResponses = await validator.validate(validatorParams);
                const valErrors = validationResponses.filter(_ => !_.valid);
                const valRejected = validationResponses.filter(_ => _.rejected);
                setValidationErrors(valErrors);
                if (valRejected.length > 0) {
                    setTimestate('Rejected');
                    return;
                }
                if (valErrors.length > 0) {
                    setShowValidationWarningDialog(true);
                    return;
                }

                if (user.autoApprove) {
                    setTimeout(() => {
                        setTimestate('Approved');
                    }, 1000);
                }
            } catch (error) {
                console.error(error);
            }
        }

        setSubmitted(state === 'Submitted' || state === 'Approved');
        setTimestate(state);

        mutateReportingPeriodState({ state, submitComment, userReportingPeriodId: selectedTimePeriod.reportPeriodId });
    };

    //TODO: NIV - Make real fix, this is a quickfix for copy timesheet
    // const refreshCurrentPeriod = async (repPeriodId: any) => {
    //     ApiCalls.getRowsByUserReportingPeriodId({ userReportingPeriodId: repPeriodId }).then(({ data: json }) => {
    //         setMytimeprojects(json);
    //         setPeriodloadingcomplete(true);
    //         setLoadingcomplete(true);
    //         refetchAllPeriods();
    //     });
    // };

    const handleCopyTimesheet = async () => {
        setPeriodloadingcomplete(false);
        mutateCopyTimesheet({
            reportPeriodId: currentPeriod.reportPeriod.reportPeriodId,
            setInformationDialogInformation: setInformationDialogInformation,
            setShowInformationDialog: setShowInformationDialog,
        });
        setPeriodloadingcomplete(true);
    };

    const closeInformationDialog = () => {
        setShowInformationDialog(false);
    };

    const RenderStatuses = () => {
        // PeriodIsClosed overrules all other statuses
        if (periodIsClosed) {
            return (
                <MessageBar
                    messageBarType={MessageBarType.info}
                    isMultiline={false}
                    dismissButtonAriaLabel="Close"
                    truncated={false}
                    overflowButtonAriaLabel="See more"
                >
                    <b>{t('message.periodClose')}</b>
                </MessageBar>
            );
        } else {
            return (
                <>
                    {timestate === 'Open' && <div className="messeagebarreplace"></div>}
                    {timestate === 'Open' && validationErrors.length > 0 && (
                        <MessageBar
                            messageBarType={MessageBarType.warning}
                            isMultiline={false}
                            dismissButtonAriaLabel="Close"
                            truncated={false}
                            overflowButtonAriaLabel="See more"
                            style={{
                                color: theme.semanticColors.bodyText,
                            }}
                        >
                            <b>{t('message.open.title')}</b>{' '}
                            <i>
                                {t('message.open.warning')}{' '}
                                {validationErrors.length === 0 ? (
                                    comment
                                ) : (
                                    <ValidationErrors color={theme.semanticColors.bodyText} validationResponses={validationErrors} />
                                )}
                            </i>
                        </MessageBar>
                    )}
                    {timestate === 'Submitted' ? (
                        <MessageBar
                            messageBarType={MessageBarType.info}
                            styles={{ root: { backgroundColor: '#9cee94' } }}
                            isMultiline={false}
                            dismissButtonAriaLabel="Close"
                            truncated={false}
                            overflowButtonAriaLabel="See more"
                        >
                            <b>{t('message.submit')}</b>
                        </MessageBar>
                    ) : (
                        <></>
                    )}
                    {timestate === 'Approved' ? (
                        <MessageBar
                            messageBarType={MessageBarType.success}
                            styles={{ root: { backgroundColor: '#9cee94' } }}
                            isMultiline={false}
                            dismissButtonAriaLabel="Close"
                            truncated={false}
                            overflowButtonAriaLabel="See more"
                        >
                            <b>{t('message.approved.title')}</b>
                            {comment === '' ? (
                                <></>
                            ) : (
                                <>
                                    <br />
                                    <b>{t('message.approved.description')}</b> <h5>{comment}</h5>
                                </>
                            )}
                        </MessageBar>
                    ) : (
                        <></>
                    )}
                    {timestate === 'Rejected' ? (
                        <MessageBar
                            messageBarType={MessageBarType.blocked}
                            styles={{ root: { backgroundColor: '#f39494' } }}
                            isMultiline={false}
                            dismissButtonAriaLabel="Close"
                            truncated={false}
                            overflowButtonAriaLabel="See more"
                        >
                            <Label
                                styles={{
                                    root: {
                                        color: theme.semanticColors.warningBackground,
                                    },
                                }}
                            >
                                {t('message.rejected.title')}
                            </Label>
                            <Label
                                styles={{
                                    root: {
                                        color: theme.semanticColors.warningBackground,
                                    },
                                }}
                            >
                                {t('message.rejected.description')}
                            </Label>
                            {validationErrors.length === 0 ? <h5>{comment}</h5> : <ValidationErrors validationResponses={validationErrors} />}
                        </MessageBar>
                    ) : (
                        <></>
                    )}
                </>
            );
        }
    };

    if (!loadingcomplete) {
        return <QuotesLoader loadingtext={t('loadingPeriods')} />;
    }

    return (
        <>
            <PageHeader noPaddingBottom>
                <Stack horizontal wrap horizontalAlign="space-between" styles={{ root: { paddingBottom: theme.spacing.m } }} /*className="filterSection"*/>
                    <Stack verticalAlign="center" horizontal>
                        <Search alreadyAddedProjects={mytimeprojects} timeAdd={addTaskFromSearch} active={timestate === 'Open' || timestate === 'Rejected'} />
                    </Stack>

                    <Stack verticalAlign="center">
                        {allPeriods && (
                            <PeriodPicker
                                selectedDateUpdated={selectedDateUpdated}
                                defaultStartDate={periodStart}
                                allPeriods={allPeriods}
                                currentTimeState={timestate}
                                showRememberSubmitCallout={ShowRememberSubmitCallout(new Date(lastPeriodEnd).removeTimeZone(), lastPeriodState)}
                                currentPeriodIsLoading={isLoading}
                                setPeriodNotCreated={setPeriodNotCreated}
                                parentSelectedDate={selectedDate}
                            />
                        )}
                    </Stack>

                    <TimeStatePicker
                        state={timestate}
                        stateChanged={timestatechanged}
                        periodIsClosed={periodIsClosed}
                        comment={comment}
                        submitComment={submitComment}
                        mytimeprojects={mytimeprojects}
                        allocations={currentPeriod?.timesheetData?.allocations ?? []}
                    />
                </Stack>
            </PageHeader>
            {periodNotCreated === true ? (
                <div className="centeredtext">
                    <h2>{t('periodNotCreated')}</h2>
                </div>
            ) : !periodloadingcomplete ? (
                <QuotesLoader loadingtext={t('loadingEntries')} />
            ) : (
                <CellContextProvider onCellUpdated={onCellUpdated}>
                    <ContentSection noPadding>
                        <div className="messeagebarContainer">
                            <div className="messeagebarContainerInner">
                                <RenderStatuses />
                            </div>
                        </div>
                    </ContentSection>
                    <PageContent>
                        <ContentSection isDetailslist>
                            {/* {gridView === 'days' ? ( */}
                            {gridView === 'period' ? (
                                <TimePeriodWeekOverview
                                    search={searchActivate}
                                    times={mytimeprojects}
                                    onCellWeekUpdated={onCellWeekUpdated}
                                    mode={TimeGridMode.MyTime}
                                    pinTime={pinTime}
                                    deleteTime={DeleteTime}
                                    today={today}
                                    submitted={submitted}
                                    reportPeriodId={selectedTimePeriod.reportPeriodId}
                                    periodIsClosed={periodIsClosed}
                                    startDate={selectedTimePeriod.startDate}
                                    endDate={selectedTimePeriod.endDate}
                                    deadlineDate={periodDeadlineDate}
                                    timesheetstate={timestate}
                                    timesheetmessage={comment}
                                    updateWorkType={updateWorkType}
                                    validationErrors={validationErrors}
                                    copyFromLastWeek={handleCopyTimesheet}
                                    onRemainingWorkUpdate={handleRemainingWorkUpdate}
                                />
                            ) : (
                                <TimePeriod
                                    search={searchActivate}
                                    times={mytimeprojects}
                                    onCellUpdated={onCellUpdated}
                                    totalCellUpdated={onCellWeekUpdated}
                                    mode={TimeGridMode.MyTime}
                                    pinTime={pinTime}
                                    deleteTime={DeleteTime}
                                    today={today}
                                    submitted={submitted}
                                    workDays={selectedTimePeriod.workDays}
                                    reportPeriodId={selectedTimePeriod.reportPeriodId}
                                    periodIsClosed={periodIsClosed}
                                    startDate={selectedTimePeriod.startDate}
                                    endDate={selectedTimePeriod.endDate}
                                    deadlineDate={periodDeadlineDate}
                                    timesheetstate={timestate}
                                    timesheetmessage={comment}
                                    updateWorkType={updateWorkType}
                                    validationErrors={validationErrors}
                                    copyFromLastWeek={handleCopyTimesheet}
                                    allocations={currentPeriod.timesheetData?.allocations ?? []}
                                    recalculateActualWork={recalculateActualWork}
                                    onRemainingWorkUpdate={handleRemainingWorkUpdate}
                                />
                            )}
                        </ContentSection>
                    </PageContent>
                </CellContextProvider>
            )}
            <Dialog
                hidden={hideDublicateTimeDlg}
                onDismiss={() => setHideDublicateTimeDlg(!hideDublicateTimeDlg)}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: t('dialog.dublicateTime.title'),
                    subText: t('dialog.dublicateTime.description'),
                }}
            >
                <DialogFooter>
                    <PrimaryButton text="OK" onClick={() => setHideDublicateTimeDlg(!hideDublicateTimeDlg)} />
                </DialogFooter>
            </Dialog>
            <ValidationWarningDialog hidden={!showValidationWarningDialog} handleDialog={handleValidationError} warnings={validationErrors} />
            <InformationDialog show={showInformationDialog} closeDialog={closeInformationDialog} information={informationDialogInformation} />
            {!isTeams && enableCoachmarks && <TFTMainCoachmarks />}
        </>
    );
}

export default Time;
